/**
 * REQ-002 MARKETING / FASE 1 (BHM-CDX-DIR-098) — Marcas + Brand Brain.
 *
 * MARCA → identidad (nombre, tono).
 * BRAND BRAIN → configuración estratégica real de esa marca (HubMarketingBrandBrain).
 * Sin métricas ficticias: no se muestra analítica; la fase de Posts vendrá después (NO_DATA).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  APPROVAL_DECISION_LABELS,
  BRAND_TONE_LABELS,
  BRAND_TONE_OPTIONS,
  POST_STATUS_LABELS,
  brandToForm,
  canApproveMarketing,
  canSubmitPost,
  canWriteMarketing,
  formToBrandBrain,
  formToPost,
  isPostEditable,
  marketingListState,
  postToForm,
  type BrandBrainForm,
  type PostForm,
} from '../modules/marketingState';
import {
  marketingApi,
  type MarketingApproval,
  type MarketingBrand,
  type MarketingBrandInput,
  type MarketingPost,
} from '../stores/marketingApi';
import { usePermissionStore } from '../stores/permissionStore';
import { useAuthStore } from '../stores/authStore';
import { isSuperAdmin } from '../utils/permissions';

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'detail'; brand: MarketingBrand };

const EMPTY_FORM: BrandBrainForm = {
  description: '',
  targetAudience: '',
  keyMessages: '',
  avoid: '',
  hashtags: '',
  competitors: '',
};

export default function MarketingPage() {
  const permissions = usePermissionStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);
  // SUPERADMIN conserva sus capacidades según el modelo vigente (el guard de rutas no
  // carga permisos para SA); el backend sigue siendo la autoridad real en escritura.
  const canWrite = isSuperAdmin(user?.role ?? '') || canWriteMarketing(permissions);
  // FASE 3 (DIR-100): aprobar es una capacidad DISTINTA de escribir.
  const canApprove = isSuperAdmin(user?.role ?? '') || canApproveMarketing(permissions);

  const [brands, setBrands] = useState<MarketingBrand[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [name, setName] = useState('');
  const [tone, setTone] = useState<string>('professional');
  const [form, setForm] = useState<BrandBrainForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      setBrands(await marketingApi.listBrands());
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // ─── FASE 2 (BHM-CDX-DIR-099) — Posts de la marca seleccionada ────────────
  const [posts, setPosts] = useState<MarketingPost[] | null>(null);
  const [postsFailed, setPostsFailed] = useState(false);
  const [postMode, setPostMode] = useState<'list' | 'create' | 'edit'>('list');
  const [activePost, setActivePost] = useState<MarketingPost | null>(null);
  const [postForm, setPostForm] = useState<PostForm>({ content: '', hashtags: '' });
  const [postSaving, setPostSaving] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  const loadPosts = useCallback(async (brandId: string) => {
    setPostsFailed(false);
    try {
      setPosts(await marketingApi.listPosts(brandId));
    } catch {
      setPostsFailed(true);
    }
  }, []);

  // ─── FASE 3 (BHM-CDX-DIR-100) — Cola de aprobaciones + historial ──────────
  const [pendingPosts, setPendingPosts] = useState<MarketingPost[]>([]);
  const [approvals, setApprovals] = useState<MarketingApproval[]>([]);
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const all = await marketingApi.listPosts();
      setPendingPosts(all.filter((p) => p.status === 'pending_approval'));
    } catch {
      // la cola falla en silencio: la lista de posts de la marca ya reporta errores
    }
  }, []);

  const loadApprovals = useCallback(async (postId: string) => {
    try {
      setApprovals(await marketingApi.listApprovals(postId));
    } catch {
      setApprovals([]);
    }
  }, []);

  const state = marketingListState({ loading: brands === null, failed, brands });

  const openCreate = () => {
    setName('');
    setTone('professional');
    setForm(EMPTY_FORM);
    setSaveError(null);
    setMode({ kind: 'create' });
  };

  const openDetail = (brand: MarketingBrand) => {
    setName(brand.name);
    setTone(brand.tone);
    setForm(brandToForm(brand));
    setSaveError(null);
    setPosts(null);
    setPostMode('list');
    setActivePost(null);
    setPostError(null);
    setMode({ kind: 'detail', brand });
    void loadPosts(brand.id);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    const brain = formToBrandBrain(form);
    const input: MarketingBrandInput = { name: name.trim(), tone, ...brain };
    try {
      if (mode.kind === 'create') {
        const created = await marketingApi.createBrand(input);
        await load();
        setMode({ kind: 'detail', brand: created });
      } else if (mode.kind === 'detail') {
        const updated = await marketingApi.updateBrand(mode.brand.id, input);
        await load();
        setMode({ kind: 'detail', brand: updated });
      }
    } catch {
      setSaveError('No se ha podido guardar. Revisa los datos e inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key: keyof BrandBrainForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ─── FASE 2 (BHM-CDX-DIR-099) — acciones reales sobre posts ───────────────
  // Sin acciones falsas: no hay Aprobar/Rechazar (bloque 100 APPROVALS) ni
  // publicación externa. La única transición ofrecida es «Enviar a aprobación».

  const openCreatePost = () => {
    setPostForm({ content: '', hashtags: '' });
    setActivePost(null);
    setPostError(null);
    setPostMode('create');
  };

  const openPost = (post: MarketingPost) => {
    setPostForm(postToForm(post));
    setActivePost(post);
    setPostError(null);
    setDecisionComment('');
    setDecisionError(null);
    setPostMode('edit');
    void loadApprovals(post.id);
    void loadPending();
  };

  const backToPosts = () => {
    setPostMode('list');
    setActivePost(null);
    setPostError(null);
    if (mode.kind === 'detail') void loadPosts(mode.brand.id);
  };

  const setPostField = (key: keyof PostForm, value: string) =>
    setPostForm((prev) => ({ ...prev, [key]: value }));

  const savePost = async () => {
    if (mode.kind !== 'detail') return;
    setPostSaving(true);
    setPostError(null);
    const payload = formToPost(postForm);
    try {
      if (postMode === 'create') {
        await marketingApi.createPost({ brandId: mode.brand.id, ...payload });
      } else if (activePost) {
        await marketingApi.updatePost(activePost.id, payload);
      }
      await loadPosts(mode.brand.id);
      setPostMode('list');
      setActivePost(null);
    } catch {
      setPostError('No se ha podido guardar el post. Revisa los datos e inténtalo de nuevo.');
    } finally {
      setPostSaving(false);
    }
  };

  const submitPost = async () => {
    if (!activePost) return;
    setPostSaving(true);
    setPostError(null);
    try {
      const updated = await marketingApi.submitPost(activePost.id);
      setActivePost(updated);
      if (mode.kind === 'detail') await loadPosts(mode.brand.id);
    } catch {
      setPostError('No se ha podido enviar a aprobación.');
    } finally {
      setPostSaving(false);
    }
  };

  /**
   * FASE 3 (DIR-100) — decisión humana. REJECTED exige comentario (validación de UI;
   * el backend lo vuelve a exigir). Tras decidir: actualizar UI, sacar el post de la
   * cola y refrescar historial. El actor lo resuelve el backend desde la autenticación.
   */
  const decide = async (decision: 'APPROVED' | 'REJECTED') => {
    if (!activePost) return;
    if (decision === 'REJECTED' && decisionComment.trim().length === 0) {
      setDecisionError('El comentario es obligatorio al rechazar.');
      return;
    }
    setDeciding(true);
    setDecisionError(null);
    try {
      const comment = decisionComment.trim();
      const updated =
        decision === 'APPROVED'
          ? await marketingApi.approvePost(activePost.id, comment || undefined)
          : await marketingApi.rejectPost(activePost.id, comment);
      setActivePost(updated);
      setDecisionComment('');
      await loadApprovals(activePost.id);
      await loadPending();
      if (mode.kind === 'detail') await loadPosts(mode.brand.id);
    } catch {
      setDecisionError('No se ha podido registrar la decisión.');
    } finally {
      setDeciding(false);
    }
  };

  const listPanel = (
    <section data-testid="marketing-brands-panel" className="w-full">
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Marketing · Marcas</h1>
        {canWrite && (
          <button
            data-testid="marketing-new-brand"
            onClick={openCreate}
            className="rounded-lg bg-[#D00E69] px-4 py-2 text-sm font-medium text-white"
          >
            Nueva marca
          </button>
        )}
      </header>

      {state === 'loading' && (
        <p data-testid="marketing-loading" className="mt-6 text-sm opacity-70">
          Cargando marcas…
        </p>
      )}
      {state === 'error' && (
        <div data-testid="marketing-error" className="mt-6 rounded-lg border border-red-300 p-4 text-sm text-red-700">
          No se han podido cargar las marcas.{' '}
          <button className="underline" onClick={() => void load()}>
            Reintentar
          </button>
        </div>
      )}
      {state === 'empty' && (
        <div data-testid="marketing-empty" className="mt-6 rounded-lg border p-6 text-sm opacity-70">
          Todavía no hay marcas. {canWrite ? 'Crea la primera marca para configurar su Brand Brain.' : 'Aún no hay marcas que mostrar.'}
        </div>
      )}
      {state === 'ready' && (
        <ul data-testid="marketing-brands-list" className="mt-4 grid gap-3">
          {brands!.map((brand) => (
            <li key={brand.id}>
              <button
                data-testid="marketing-brand-row"
                onClick={() => openDetail(brand)}
                className="w-full rounded-lg border p-4 text-left hover:border-[#D00E69]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{brand.name}</span>
                  <span className="rounded-full border px-2 py-0.5 text-xs">
                    {BRAND_TONE_LABELS[brand.tone] ?? brand.tone}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm opacity-70">{brand.description || 'Sin descripción'}</p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* FASE 3 (DIR-100) — Cola real de APROBACIONES (posts pending de la org). */}
      <div data-testid="marketing-approvals-queue" className="mt-6 rounded-lg border p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide opacity-70">Aprobaciones</h2>
        {pendingPosts.length === 0 ? (
          <p data-testid="marketing-approvals-empty" className="mt-3 text-sm opacity-70">
            No hay posts pendientes de aprobación.
          </p>
        ) : (
          <ul className="mt-3 grid gap-2">
            {pendingPosts.map((post) => {
              const brand = brands?.find((b) => b.id === post.brandId);
              return (
                <li key={post.id}>
                  <button
                    data-testid="marketing-approval-row"
                    onClick={() => {
                      if (brand) {
                        openDetail(brand);
                        openPost(post);
                      }
                    }}
                    className="w-full rounded-lg border p-3 text-left hover:border-[#D00E69]"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs uppercase tracking-wide opacity-70">
                        {brand?.name ?? post.brandId}
                      </span>
                      <span data-testid="marketing-approval-row-status" className="rounded-full border px-2 py-0.5 text-xs">
                        Pendiente de aprobación
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm">{post.content}</p>
                    {post.updatedAt && (
                      <p className="mt-1 text-xs opacity-60">{new Date(post.updatedAt).toLocaleString('es-ES')}</p>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );

  const detailPanel =
    mode.kind === 'detail' || mode.kind === 'create' ? (
      <section data-testid="marketing-brand-detail" className="w-full">
        <button data-testid="marketing-back" className="text-sm underline" onClick={() => setMode({ kind: 'list' })}>
          ← Volver a marcas
        </button>

        {/* MARCA — identidad */}
        <div className="mt-4 rounded-lg border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide opacity-70">Marca</h2>
          <div className="mt-3 grid gap-3">
            <label className="grid gap-1 text-sm">
              Nombre
              <input
                data-testid="marketing-brand-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canWrite}
                className="rounded-lg border px-3 py-2"
                placeholder="Nombre real de la marca"
              />
            </label>
            <label className="grid gap-1 text-sm">
              Tono
              <select
                data-testid="marketing-brand-tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                disabled={!canWrite}
                className="rounded-lg border px-3 py-2"
              >
                {BRAND_TONE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {BRAND_TONE_LABELS[opt]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* BRAND BRAIN — configuración estratégica */}
        <div className="mt-4 rounded-lg border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide opacity-70">Brand Brain</h2>
          <div className="mt-3 grid gap-3">
            <label className="grid gap-1 text-sm">
              Descripción
              <textarea
                data-testid="marketing-brain-description"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                disabled={!canWrite}
                rows={3}
                className="rounded-lg border px-3 py-2"
                placeholder="Quién es la marca y qué ofrece"
              />
            </label>
            <label className="grid gap-1 text-sm">
              Público objetivo
              <textarea
                data-testid="marketing-brain-audience"
                value={form.targetAudience}
                onChange={(e) => setField('targetAudience', e.target.value)}
                disabled={!canWrite}
                rows={2}
                className="rounded-lg border px-3 py-2"
                placeholder="A quién se dirige"
              />
            </label>
            <label className="grid gap-1 text-sm">
              Mensajes clave (uno por línea)
              <textarea
                data-testid="marketing-brain-key-messages"
                value={form.keyMessages}
                onChange={(e) => setField('keyMessages', e.target.value)}
                disabled={!canWrite}
                rows={3}
                className="rounded-lg border px-3 py-2"
              />
            </label>
            <label className="grid gap-1 text-sm">
              A evitar (uno por línea)
              <textarea
                data-testid="marketing-brain-avoid"
                value={form.avoid}
                onChange={(e) => setField('avoid', e.target.value)}
                disabled={!canWrite}
                rows={3}
                className="rounded-lg border px-3 py-2"
              />
            </label>
            <label className="grid gap-1 text-sm">
              Hashtags (uno por línea)
              <textarea
                data-testid="marketing-brain-hashtags"
                value={form.hashtags}
                onChange={(e) => setField('hashtags', e.target.value)}
                disabled={!canWrite}
                rows={2}
                className="rounded-lg border px-3 py-2"
              />
            </label>
            <label className="grid gap-1 text-sm">
              Competidores (uno por línea)
              <textarea
                data-testid="marketing-brain-competitors"
                value={form.competitors}
                onChange={(e) => setField('competitors', e.target.value)}
                disabled={!canWrite}
                rows={2}
                className="rounded-lg border px-3 py-2"
              />
            </label>
          </div>
        </div>

        {/* POSTS (FASE 2 — BHM-CDX-DIR-099) — lista real por marca. Sin métricas:
            la analítica del contrato (HubMarketingPostAnalytics) queda NO_DATA hasta
            disponer de fuente real; no se muestran ceros ni datos inventados. */}
        {mode.kind === 'detail' && (
          <div data-testid="marketing-posts-panel" className="mt-4 rounded-lg border p-4">
            <header className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide opacity-70">Posts</h2>
              {canWrite && postMode === 'list' && (
                <button
                  data-testid="marketing-new-post"
                  onClick={openCreatePost}
                  className="rounded-lg bg-[#D00E69] px-3 py-1.5 text-sm font-medium text-white"
                >
                  Nuevo post
                </button>
              )}
            </header>

            {postMode === 'list' && (
              <>
                {posts === null && !postsFailed && (
                  <p data-testid="marketing-posts-loading" className="mt-3 text-sm opacity-70">
                    Cargando posts…
                  </p>
                )}
                {postsFailed && (
                  <div data-testid="marketing-posts-error" className="mt-3 rounded-lg border border-red-300 p-3 text-sm text-red-700">
                    No se han podido cargar los posts.{' '}
                    <button className="underline" onClick={() => void loadPosts(mode.kind === 'detail' ? mode.brand.id : '')}>
                      Reintentar
                    </button>
                  </div>
                )}
                {posts !== null && !postsFailed && posts.length === 0 && (
                  <div data-testid="marketing-posts-empty" className="mt-3 rounded-lg border p-4 text-sm opacity-70">
                    Todavía no hay posts para esta marca.
                  </div>
                )}
                {posts !== null && !postsFailed && posts.length > 0 && (
                  <ul data-testid="marketing-posts-list" className="mt-3 grid gap-2">
                    {posts.map((post) => (
                      <li key={post.id}>
                        <button
                          data-testid="marketing-post-row"
                          onClick={() => openPost(post)}
                          className="w-full rounded-lg border p-3 text-left hover:border-[#D00E69]"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <span data-testid="marketing-post-content" className="line-clamp-2 text-sm">
                              {post.content}
                            </span>
                            <span
                              data-testid="marketing-post-status-pill"
                              className="shrink-0 rounded-full border px-2 py-0.5 text-xs"
                            >
                              {POST_STATUS_LABELS[post.status] ?? post.status}
                            </span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {postMode !== 'list' && (
              <div className="mt-3 grid gap-3">
                <label className="grid gap-1 text-sm">
                  Contenido
                  <textarea
                    data-testid="marketing-post-content-input"
                    value={postForm.content}
                    onChange={(e) => setPostField('content', e.target.value)}
                    disabled={!canWrite || (activePost !== null && !isPostEditable(activePost.status))}
                    rows={4}
                    className="rounded-lg border px-3 py-2"
                    placeholder="Texto real del post"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  Hashtags (uno por línea)
                  <textarea
                    data-testid="marketing-post-hashtags"
                    value={postForm.hashtags}
                    onChange={(e) => setPostField('hashtags', e.target.value)}
                    disabled={!canWrite || (activePost !== null && !isPostEditable(activePost.status))}
                    rows={2}
                    className="rounded-lg border px-3 py-2"
                  />
                </label>

                {activePost && (
                  <p data-testid="marketing-post-status-detail" className="text-sm">
                    Estado: {POST_STATUS_LABELS[activePost.status] ?? activePost.status}
                  </p>
                )}

                {activePost && activePost.status === 'pending_approval' && (
                  <p data-testid="marketing-post-pending-note" className="rounded-lg border px-3 py-2 text-sm opacity-70">
                    Pendiente de aprobación
                  </p>
                )}

                {/* FASE 3 (DIR-100) — Decisión humana. Solo marketing.approve ve acciones;
                    write sin approve solo consulta (el backend devuelve 403 igualmente). */}
                {activePost && activePost.status === 'pending_approval' && canApprove && (
                  <div data-testid="marketing-decision-panel" className="grid gap-2 rounded-lg border p-3">
                    <label className="grid gap-1 text-sm">
                      Comentario de decisión (opcional al aprobar, obligatorio al rechazar)
                      <textarea
                        data-testid="marketing-decision-comment"
                        value={decisionComment}
                        onChange={(e) => setDecisionComment(e.target.value)}
                        rows={2}
                        className="rounded-lg border px-3 py-2"
                      />
                    </label>
                    {decisionError && (
                      <p data-testid="marketing-decision-error" className="text-sm text-red-700">
                        {decisionError}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        data-testid="marketing-approve-btn"
                        onClick={() => void decide('APPROVED')}
                        disabled={deciding}
                        className="rounded-lg bg-[#D00E69] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                      >
                        Aprobar
                      </button>
                      <button
                        data-testid="marketing-reject-btn"
                        onClick={() => void decide('REJECTED')}
                        disabled={deciding || decisionComment.trim().length === 0}
                        className="rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50"
                      >
                        Rechazar
                      </button>
                    </div>
                  </div>
                )}
                {activePost && activePost.status === 'pending_approval' && !canApprove && (
                  <p data-testid="marketing-decision-readonly" className="rounded-lg border px-3 py-2 text-sm opacity-70">
                    Solo usuarios con permiso de aprobación pueden aprobar o rechazar.
                  </p>
                )}

                {/* Historial real de decisiones (decisión, actor real, fecha, comentario). */}
                {activePost && (
                  <div data-testid="marketing-approval-history" className="grid gap-2 rounded-lg border p-3">
                    <h3 className="text-sm font-semibold uppercase tracking-wide opacity-70">Historial de aprobación</h3>
                    {approvals.length === 0 ? (
                      <p data-testid="marketing-approval-history-empty" className="text-sm opacity-70">
                        Sin decisiones registradas.
                      </p>
                    ) : (
                      <ul className="grid gap-2">
                        {approvals.map((a) => (
                          <li key={a.id} data-testid="marketing-approval-history-row" className="rounded-lg border p-2 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{APPROVAL_DECISION_LABELS[a.decision] ?? a.decision}</span>
                              <span className="text-xs opacity-60">
                                {a.createdAt ? new Date(a.createdAt).toLocaleString('es-ES') : ''}
                              </span>
                            </div>
                            <p className="text-xs opacity-70">Actor: {a.actorId}</p>
                            {a.comment && <p className="mt-1">{a.comment}</p>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {postError && (
                  <p data-testid="marketing-post-error" className="text-sm text-red-700">
                    {postError}
                  </p>
                )}

                {canWrite && (activePost === null || isPostEditable(activePost.status)) && (
                  <button
                    data-testid="marketing-post-save"
                    onClick={() => void savePost()}
                    disabled={postSaving || postForm.content.trim().length === 0}
                    className="rounded-lg bg-[#D00E69] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {postSaving ? 'Guardando…' : 'Guardar'}
                  </button>
                )}
                {canWrite && activePost !== null && canSubmitPost(activePost.status) && (
                  <button
                    data-testid="marketing-post-submit"
                    onClick={() => void submitPost()}
                    disabled={postSaving}
                    className="rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    Enviar a aprobación
                  </button>
                )}

                <button data-testid="marketing-post-back" className="text-sm underline" onClick={backToPosts}>
                  ← Volver a posts
                </button>
              </div>
            )}
          </div>
        )}

        {saveError && (
          <p data-testid="marketing-save-error" className="mt-3 text-sm text-red-700">
            {saveError}
          </p>
        )}
        {canWrite && (
          <button
            data-testid="marketing-save"
            onClick={() => void save()}
            disabled={saving || name.trim().length === 0}
            className="mt-4 rounded-lg bg-[#D00E69] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        )}
      </section>
    ) : null;

  return (
    <main data-testid="marketing-page" className="w-full max-w-full px-4 py-6">
      {mode.kind === 'list' ? listPanel : detailPanel}
    </main>
  );
}
